import React, { useState } from 'react';
import Box from '@mui/material/Box';
import { toast } from 'react-toastify';
import './OrganizationDetailsCard.css';
import { useTheme } from '@mui/material/styles';
import ConfirmDialog from 'components/ConfirmDialog';
import EditOrganizationModal from '../OrgComponets/EditOrganizationModal/EditOrganizationModal';
import OrgImage from '../OrgComponets/OrgTile/OrgImage';
import BranchesServices from 'services/BranchesServices';

const OrganizationTab = ({ org, contactInfo = {name: 'NA',email:'NA',phone:'NA', address:'NA'}, onStatusChange, onEditLoading, onEditRefresh }) => {

  const theme = useTheme();
  const [active, setActive] = useState(org.status === 'Active');
  const [editOpen, setEditOpen] = useState(false);
  const [branchData, setBranchData] = useState(null);

  React.useEffect(() => {
    const fetchBranchData = async () => {
      try {
        if (org?.id) {
          const response = await BranchesServices.get({ organization_id: org.id });
          if (response && response.success && response.data?.view?.data && response.data.view.data.length > 0) {
            const branches = response.data.view.data;
            const mainBranch = branches.find(branch => 
              branch.is_main === true
            );
            setBranchData(mainBranch || null);
          }
        }
      } catch (error) {
        console.error('Error fetching branch data:', error);
      }
    };

    fetchBranchData();
  }, [org?.id]);

  if (!org) {
    return <div>No organization data found.</div>;
  }
  return (
    <Box
      className="border-1 border-radius-12"
      sx={{
        background: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderRadius: 12,
        position: 'relative',
        padding: 0,
        boxShadow: '0 4px 16px 0 rgba(0,0,0,0.10)',
        border: '1.5px solid #e0e0e0',
        marginTop:8,
      }}
    >
      <div className="org-card-header " style={{ 
        background: theme.palette.background.default,
        borderRadius: '10px 10px 0 0', 
        padding: '20px 16px', 
        margin: 0 
      }}>
        <div className="org-logo-section">
          <div className="org-logo">
            <OrgImage
              orgId={org.id}
              logo={org.logo}
              alt={org.short_name || 'Organization Logo'}
              width={64}
              height={64}
              showFullscreen={false}
              fallbackSrc="/assets/images/no-header-img.jpg"
              cacheBuster={org.logo}
            />
          </div>
          <div className="org-title-section">
            <h2 className="org-title" style={{ color: theme.palette.text.primary }}>{org.name}</h2>
            <p className="org-subtitle" style={{ color: theme.palette.text.secondary }}>{org.short_name}</p>
            <div className="org-status" style={{ color: theme.palette.text.secondary }}>
              <span>Color:</span>
              <div 
                className="color-dot" 
                style={{ backgroundColor: org.color_code, borderColor: theme.palette.text.secondary }}
              ></div>
            </div>
          </div>
        </div>
        <div className="org-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16 }}>
          <button className="edit-btn" style={{ background: theme.palette.OrgCardColor?.editbtn || '#E58653' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.palette.OrgCardColor?.keywordText || 'white'} strokeWidth="2" onClick={() => setEditOpen(true)} style={{ cursor: 'pointer' }}>
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="m18.5 2.5 a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <EditOrganizationModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            initialData={{
              id: org.id, 
              organizationName: org.name || '',
              shortName: org.short_name || '',
              color: org.color_code || '',
              ...org 
            }}
            onSave={async () => {
              setEditOpen(false);
              if (typeof onEditLoading === 'function') onEditLoading(true);
              toast.success('The organization is successfully updated.');
              if (typeof onEditRefresh === 'function') await onEditRefresh();
              if (typeof onEditLoading === 'function') onEditLoading(false);
            }}
          />
          <div className="toggle-section" style={{ marginLeft: 'auto' }}>
            <span className="toggle-label" style={{ color: '#e9625f' }}>{active ? 'Active' : 'Deactive'}</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={active}
                onChange={async () => {
                  const newStatus = !active;
                  ConfirmDialog({
                    type: 'warning',
                    title: 'Confirm Action',
                    description: `Are you sure you want to ${newStatus ? 'activate' : 'deactivate'} this organization?`,
                    buttons: [
                      {
                        label: 'Cancel',
                        onClick: () => {
                        }
                      },
                      {
                        label: 'Confirm',
                        onClick: async () => {
                          setActive(newStatus);
                          if (typeof onStatusChange === 'function') {
                            await onStatusChange(newStatus);
                          }
                          if (!newStatus) {
                            toast.success('Organization successfully deactivated.');
                          } else {
                            toast.success('Organization successfully activated.');
                          }
                        }
                      }
                    ]
                  });
                }}
              />
              <span
                className="toggle-slider"
                style={{ background: active ? theme.palette.OrgCardColor?.activebtn || '#4CAF50' : theme.palette.OrgCardColor?.deactivebtn || '#EB5D5D' }}
              ></span>
            </label>
          </div>
        </div>
      </div>

      <div className="org-card-content" style={{ padding: '24px' }}>
        <div className="org-description">
          <h3 style={{ color: theme.palette.text.primary }}>Organization description</h3>
          <p style={{ color: theme.palette.text.secondary }}>{org.description || ''}</p>
        </div>

        <div
          className="org-contact"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 20,
          }}
        >
          <div className="contact-item" style={{ background: 'transparent', color: theme.palette.text.primary }}>
            <div className="contact-icon" style={{ border: `2px solid ${theme.palette.primary.main}`, borderRadius: 8, padding: 4 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.palette.text.primary} strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <span style={{ color: theme.palette.text.primary }}>{contactInfo.name}</span>
          </div>
          <div className="contact-item" style={{ background: 'transparent', color: theme.palette.text.primary }}>
            <div className="contact-icon" style={{ border: `2px solid ${theme.palette.primary.main}`, borderRadius: 8, padding: 4 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.palette.text.primary} strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <span style={{ color: theme.palette.text.primary }}>{contactInfo.email}</span>
          </div>
          <div className="contact-item" style={{ background: 'transparent', color: theme.palette.text.primary }}>
            <div className="contact-icon" style={{ border: `2px solid ${theme.palette.primary.main}`, borderRadius: 8, padding: 4 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.palette.text.primary} strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 1 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 1 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
            </div>
            <span style={{ color: theme.palette.text.primary }}>{contactInfo.phone || contactInfo.contact_no}</span>
          </div>
          <div className="contact-item" style={{ background: 'transparent', color: theme.palette.text.primary }}>
            <div className="contact-icon" style={{ border: `2px solid ${theme.palette.primary.main}`, borderRadius: 8, padding: 4 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.palette.text.primary} strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <span style={{ color: theme.palette.text.primary }}>{branchData?.address_line1 || org.address || 'N/A'}</span>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 600px) {
          .org-contact {
            display: flex !important;
            flex-direction: column !important;
            gap: 16px !important;
          }
        }
      `}</style>
    </Box>
  );
};

export default OrganizationTab;
